import {
  CALL_API,
  REQUEST_DELETE,
  REQUEST_GET,
  REQUEST_POST,
  REQUEST_PUT
} from '../middleware/api'
import { USER, USER_COLLECTION, NONE } from '../schemas'
import { decamelize } from 'humps'
import { SUCCESS, ERROR } from '../components/AlertListItem'

export const CHECK_USER_EXISTS_REQUEST = 'tru/users/CHECK_USER_EXISTS_REQUEST'
export const CHECK_USER_EXISTS_SUCCEEDED =
  'tru/users/CHECK_USER_EXISTS_SUCCEEDED'
export const CHECK_USER_EXISTS_FAILED = 'tru/users/CHECK_USER_EXISTS_FAILED'
export const CLEAR_USERS = 'tru/users/CLEAR_USERS'
export const CLEAR_SELECTED_USERS = 'tru/users/CLEAR_SELECTED_USERS'
export const CREATE_USER_REQUEST = 'tru/users/CREATE_USER_REQUEST'
export const CREATE_USER_SUCCEEDED = 'tru/users/CREATE_USER_SUCCEEDED'
export const CREATE_USER_FAILED = 'tru/users/CREATE_USER_FAILED'
export const FETCH_USERS_REQUEST = 'tru/users/FETCH_USERS_REQUEST'
export const FETCH_USERS_SUCCEEDED = 'tru/users/FETCH_USERS_SUCCEEDED'
export const FETCH_USERS_FAILED = 'tru/users/FETCH_USERS_FAILED'
export const FETCH_USER_REQUEST = 'tru/users/FETCH_USER_REQUEST'
export const FETCH_USER_SUCCEEDED = 'tru/users/FETCH_USER_SUCCEEDED'
export const FETCH_USER_FAILED = 'tru/users/FETCH_USER_FAILED'
export const SEND_WELCOME_EMAIL_REQUEST = 'tru/users/SEND_WELCOME_EMAIL_REQUEST'
export const SEND_WELCOME_EMAIL_SUCCEEDED =
  'tru/users/SEND_WELCOME_EMAIL_SUCCEEDED'
export const SEND_WELCOME_EMAIL_FAILED = 'tru/users/SEND_WELCOME_EMAIL_FAILED'
export const TOGGLE_ACTIVATE_USER_REQUEST =
  'tru/users/TOGGLE_ACTIVATE_USER_REQUEST'
export const TOGGLE_ACTIVATE_USER_SUCCEEDED =
  'tru/users/TOGGLE_ACTIVATE_USER_SUCCEEDED'
export const TOGGLE_ACTIVATE_USER_FAILED =
  'tru/users/TOGGLE_ACTIVATE_USER_FAILED'
export const TOGGLE_USER = 'tru/users/TOGGLE_USER'
export const UPDATE_USER_REQUEST = 'tru/users/UPDATE_USER_REQUEST'
export const UPDATE_USER_SUCCEEDED = 'tru/users/UPDATE_USER_SUCCEEDED'
export const UPDATE_USER_FAILED = 'tru/users/UPDATE_USER_FAILED'

export const createUser = (company, user) => ({
  [CREATE_USER_SUCCEEDED]: {
    alert: {
      type: SUCCESS,
      message: 'User successfully created'
    },
    location: '/users/managed'
  },
  [CREATE_USER_FAILED]: {
    alert: {
      type: ERROR,
      message: 'There was a problem creating the user. Please try again'
    }
  },
  [CALL_API]: {
    types: [CREATE_USER_REQUEST, CREATE_USER_SUCCEEDED, CREATE_USER_FAILED],
    endpoint: `companies/${company.id}/users.json`,
    requestType: REQUEST_POST,
    schema: USER,
    data: { user }
  }
})

export const fetchUserInCompany = (company, id) => ({
  [CALL_API]: {
    types: [FETCH_USER_REQUEST, FETCH_USER_SUCCEEDED, FETCH_USER_FAILED],
    requestType: REQUEST_GET,
    endpoint: `companies/${company.id}/users/${id}.json`,
    schema: USER
  }
})

export const updateUser = (company, user) => ({
  [UPDATE_USER_SUCCEEDED]: {
    alert: {
      type: SUCCESS,
      message: 'User successfully updated'
    }
  },
  [CALL_API]: {
    types: [UPDATE_USER_REQUEST, UPDATE_USER_SUCCEEDED, UPDATE_USER_FAILED],
    endpoint: `companies/${company.id}/users/${user.id}.json`,
    requestType: REQUEST_PUT,
    schema: USER,
    data: { user }
  }
})

export const sendWelcomeEmail = (company, user) => ({
  [SEND_WELCOME_EMAIL_SUCCEEDED]: {
    alert: {
      type: SUCCESS,
      message: `Welcome email sent to ${user.email}`
    }
  },
  [SEND_WELCOME_EMAIL_FAILED]: {
    alert: {
      type: ERROR,
      message: 'There was an error processing your request'
    }
  },
  [CALL_API]: {
    types: [
      SEND_WELCOME_EMAIL_REQUEST,
      SEND_WELCOME_EMAIL_SUCCEEDED,
      SEND_WELCOME_EMAIL_FAILED
    ],
    endpoint: `companies/${company.id}/users/${user.id}/send_welcome_notification.json`,
    requestType: REQUEST_POST,
    schema: NONE
  }
})

export const fetchUsersInCompany = (
  company,
  {
    type,
    page = 1,
    perPage = 30,
    query,
    sort = 'firstName',
    order = 'asc'
  } = {}
) => ({
  [CALL_API]: {
    types: [FETCH_USERS_REQUEST, FETCH_USERS_SUCCEEDED, FETCH_USERS_FAILED],
    endpoint: `companies/${company.id}/users/${type}.json`,
    requestType: REQUEST_GET,
    data: {
      order,
      page,
      perPage,
      query,
      sort: decamelize(sort)
    },
    dataGetter: data => data.users,
    schema: USER_COLLECTION
  }
})

// Implemented as part of "quick and dirty" add user to company interface
//   Rethink when overhauling with a more complete solution
export const fetchAdminUsersInCompany = company => ({
  [CALL_API]: {
    types: [FETCH_USERS_REQUEST, FETCH_USERS_SUCCEEDED, FETCH_USERS_FAILED],
    endpoint: `admin/companies/${company.id}/users.json`,
    requestType: REQUEST_GET,
    dataGetter: data => data.users,
    schema: USER_COLLECTION
  }
})

export const fetchUsers = ({ page = 1, perPage = 30, query }) => ({
  [FETCH_USERS_REQUEST]: { page, query },
  [CALL_API]: {
    types: [FETCH_USERS_REQUEST, FETCH_USERS_SUCCEEDED, FETCH_USERS_FAILED],
    endpoint: `users.json`,
    requestType: REQUEST_GET,
    data: { page, perPage, query },
    dataGetter: data => data.users,
    schema: USER_COLLECTION
  }
})

export const activateUserInCompany = (company, user) => ({
  [TOGGLE_ACTIVATE_USER_SUCCEEDED]: {
    entities: {
      users: {
        [user.id]: {
          userCompany: { deleteFl: false }
        }
      }
    },
    alert: {
      type: SUCCESS,
      message: user.email + I18n.t(' has been activated.')
    }
  },
  [TOGGLE_ACTIVATE_USER_FAILED]: {
    alert: {
      type: ERROR,
      message: 'There was an error processing your request'
    }
  },
  [CALL_API]: {
    types: [
      TOGGLE_ACTIVATE_USER_REQUEST,
      TOGGLE_ACTIVATE_USER_SUCCEEDED,
      TOGGLE_ACTIVATE_USER_FAILED
    ],
    endpoint: `companies/${company.id}/users/${user.id}/activate.json`,
    requestType: REQUEST_PUT,
    schema: NONE
  }
})

export const removeUserFromCompany = (company, user) => ({
  [TOGGLE_ACTIVATE_USER_SUCCEEDED]: {
    entities: {
      users: {
        [user.id]: {
          userCompany: { deleteFl: true }
        }
      }
    },
    alert: {
      type: SUCCESS,
      message: user.email + I18n.t(' has been deactivated.')
    }
  },
  [TOGGLE_ACTIVATE_USER_FAILED]: {
    alert: {
      type: ERROR,
      message: 'There was an error processing your request'
    }
  },
  [CALL_API]: {
    types: [
      TOGGLE_ACTIVATE_USER_REQUEST,
      TOGGLE_ACTIVATE_USER_SUCCEEDED,
      TOGGLE_ACTIVATE_USER_FAILED
    ],
    endpoint: `companies/${company.id}/users/${user.id}.json`,
    requestType: REQUEST_DELETE,
    schema: NONE
  }
})

export const checkUserExists = (company, email) => ({
  [CHECK_USER_EXISTS_SUCCEEDED]: {
    location: ({ response: { existsInCompany, user } }) => {
      if (existsInCompany) {
        return `/users/${user.id}/edit`
      }
      return false
    }
  },
  [CALL_API]: {
    types: [
      CHECK_USER_EXISTS_REQUEST,
      CHECK_USER_EXISTS_SUCCEEDED,
      CHECK_USER_EXISTS_FAILED
    ],
    endpoint: `companies/${company.id}/users/exists.json`,
    requestType: REQUEST_GET,
    data: { email },
    dataGetter: response => response.user,
    schema: USER
  }
})

export const toggleUser = (userId, selected) => ({
  type: TOGGLE_USER,
  userId,
  selected
})

// Implemented as part of "quick and dirty" add user to company interface
//   Rethink when overhauling with a more complete solution
export const addUsersToProjects = (userIds, projectIds, roleId) => ({
  ADD_USERS_TO_PROJECT_FAILED: {
    alert: {
      type: ERROR,
      message:
        'Failed to add users to projects. You may have forgotten to ' +
        'select users or select projects'
    }
  },
  ADD_USERS_TO_PROJECT_SUCCESS: {
    alert: {
      type: SUCCESS,
      message:
        'You have successfully requested the addition of ' +
        `${userIds.length} user(s) to ${projectIds.length} project(s). Be ` +
        'aware that this may take up to an hour to add all users depending ' +
        'on how many were selected'
    }
  },
  [CALL_API]: {
    types: [
      'ADD_USERS_TO_PROJECT_REQUEST',
      'ADD_USERS_TO_PROJECT_SUCCESS',
      'ADD_USERS_TO_PROJECT_FAILED'
    ],
    endpoint: `admin/user_role_projects.json`,
    requestType: REQUEST_POST,
    data: { userIds, projectIds, roleId },
    schema: NONE
  }
})

export const clearUsers = () => ({ type: CLEAR_USERS })
export const clearSelectedUsers = () => ({ type: CLEAR_SELECTED_USERS })



// WEBPACK FOOTER //
// ../src/actions/userActions.js